/**
 * @flow
 * @format
 */

/* global __DEVL__ */

import _ from 'lodash';
import { delay } from 'redux-saga';
import { put, call, all, takeLatest, take, race, select, spawn } from 'redux-saga/effects';
import * as API from '../services/api';
import { constants, actions } from '../actions/design';
import { getValuesForEval, getCurrentStep, getFieldValues, getDesignName } from '../selectors/design';
import { actions as errorActions } from '../actions/errors';
import { isNumeric, countDecimals, asyncCancelOnTake, downloadBlob } from '../utils';
import createValidator from '../utils/createValidator';
import { isNumber } from '../utils/validators';
import { getStepByField } from '../utils/stepHelpers';

import designRoutines from '../routines/design';
import globalRoutines from '../routines/global';

// import Rollbar from '../utils/rollbar';

// eslint-disable-next-line no-console
const devLog = __DEVL__ ? (...strs) => console.log(strs) : _.noop;

const CHART_FIGURE_ID_ARRAY = Array(12).fill(0).map((v, i) => `Figure${i + 1}`);

const TIMEOUT = Number(process.env.REACT_APP_API_TIMEOUT);
const MAX_DECIMAL_PLACES = Number(process.env.REACT_APP_MAX_DECIMAL_PLACES);
const EVALUATE_DELAY = Number(process.env.REACT_APP_API_DELAY);

const buildPayload = (action: any, state: any = {}): any => {
  let field: string = _.get(action, ['payload', 'field']);

  // Fix some wonky API issues - pretty hacky.
  if (field === 'K_DeltaI') field = 'K_DeltaI_Entry';

  const data = _.reduce(
    state,
    (obj: any, val: any, key: string) => {
      // Check against valid fields
      if (!getStepByField(key)) return obj;

      const value = _.get(state[key], 'currentValue');
      return _.set(obj, key, { currentValue: isNumeric(value) ? Number(value) : value });
    },
    {},
  );

  return data;
};

// eslint-disable-next-line no-unused-vars
function* errorHelper(err, message) {
  const statusCode: number = _.get(err, ['response', 'status'], '-1');
  if (statusCode === 401) {
    yield call(
      delay,
      Number(process.env.REACT_APP_API_REDIRECT_TIMEOUT) || 10000,
    );
    yield call(API.loginRedirect);
  }
  // We should handle this properly
  // else {
  //   yield put(
  //     errorActions.apiError({
  //       status: statusCode.toString(),
  //       message,
  //     }),
  //   );
  // }
}

/**
 * Generic helper for making API calls and building a payload from the design state.
 *
 * @private
 * @param {Function} apiFn Service API function
 * @param {Routine} routineFn Saga Routine function
 * @param {string} action Action type
 * @param {(string[] | (() => boolean))} [cb=[]] Cancel Callback
 * @returns Iterator
 */

function* helperCallApi(
  apiFn,
  routineFn: Function = () => {},
  action = null,
  cb?: string[] | (() => boolean) = [],
) {
  let result = {};

  function* fetchData(data) {
    yield put(routineFn.request());
    const res = yield call(apiFn, data);

    devLog(res);
    /**
     * Non connection related API specfic errors have a type = 'ERROR'
     * and need to be handled here as they are not necessarily caught
     * in the try/catch of `helperCallApi`.
     */
    if (res && res.response && res.response.type === 'ERROR') {
      yield put(routineFn.failure(res.response.content));
      return;
    }

    yield put(routineFn.success(res));
  }

  try {
    let cancelFn;
    let finalPayload: { [key: string]: string } = {};

    // TODO: shouldn't be modifying the payload here
    const actionType = action && action.type;

    switch (actionType) {
      case designRoutines.saveDesignRecord.TRIGGER: {
        const payloadData = yield select(getFieldValues);
        const recordName = _.get(action, 'payload', '');

        finalPayload = {
          actionEvent: { recordName },
          data: buildPayload('', payloadData),
        };

        // finalPayload = buildPayload('save', payloadData);
        // finalPayload.actionEvent.recordName = action.payload;
        break;
      }

      case designRoutines.updateRecordName.TRIGGER:
      case designRoutines.copyDesign.TRIGGER:
      case designRoutines.deleteDesign.TRIGGER:
      case 'FETCH_DESIGN/TRIGGER': {
        finalPayload = action.payload;
        break;
      }
      default: {
        const payloadData = yield select(getValuesForEval);
        finalPayload = {
          data: buildPayload(null, payloadData),
        };

        if (actionType === designRoutines.updateDesign.TRIGGER) {
          finalPayload.id = action.payload.id;
        }
        break;
      }
    }

    if (typeof cb === 'function') {
      cancelFn = cb;
    } else {
      const a = [].concat(
        [
          // appActions.jumpToStep.toString(),
          constants.STEP_FORWARD,
          constants.STEP_BACKWARD,
        ],
        cb,
      );
      cancelFn = cancelAction => a.includes(cancelAction.type);
    }

    result = yield race({
      task: call(fetchData, finalPayload),
      cancel: take(cancelFn),
    });
  } catch (err) {
    yield put(routineFn.failure(err.message));
    yield call(
      errorHelper,
      err,
      `API call for ${routineFn.toString()} was not completed.`,
    );
  } finally {
    devLog(result);
    yield put(routineFn.fulfill({ cancelled: result.cancel !== undefined }));
  }
}

function helperAPICallRouter(type: string) {
  const routine = globalRoutines[type] || designRoutines[type];
  const fn = API[type];

  if (__DEVL__) {
    /* eslint-disable no-console */
    if (!type || !routine) {
      console.warn(
        `Invalid or missing Routine for '${type}' (check 'routines/**')`,
      );
    }
    if (typeof fn !== 'function') {
      console.warn(
        `API function for '${type}' missing (check 'services/api.js')`,
      );
    }
    /* eslint-enable no-console */
  }

  // Not sure why I can't return the helper from here.. but we need an intemediary function and I don't
  // have time to figure out why.
  function* handler(action) {
    devLog(`${routine} calling ${fn}`);
    yield helperCallApi(fn, routine, action);
  }

  return takeLatest(routine.toString(), handler);
}


export function* fetchInitialData(): Generator<*, *, *> {
  try {
    yield put(actions.pendingAction());
    const { response: res1, error: err1 } = yield call(API.fetchDefaultData);
    const { response: res2, error: err2 } = yield call(API.fetchTypeInfoList);

    if (!err1 && !err2) {
      // TODO: This could be handled better somehwere else I think
      res1.data._TypeInfo = res2; // eslint-disable-line no-underscore-dangle
      yield put(actions.receiveData(res1));
    }
  } catch (err) {
    const statusCode: number = _.get(err, ['response', 'status']);
    if (statusCode === 401) {
      yield put(API.loginRedirect());
    } else {
      yield put(errorActions.apiError({
        status: '-1',
        message: 'API fetch failed.',
      }));
      // Rollbar.critical(`API fetch error: ${err.message}`);
    }
  }
}

export function* evaluateReport(): Generator<*, *, *> {
  const state = yield select(getFieldValues);
  // const step: string = yield select(getCurrentStep);
  const payload = {
    data: buildPayload('', state),
  };

  try {
    yield put(actions.requestChartData());

    const result = yield race({
      // invalid: call(checkValidFields),
      evaluate: call(API.evaluateData, payload),
      timeout: call(delay, TIMEOUT),
    });

    if (_.get(result, 'timeout')) {
      yield put(actions.evaluateError({
        field: 'all',
        status: '-1',
        message: `Evaluation took longer than expected (<${TIMEOUT / 1000}s).`,
      }));

      // Rollbar.warning(`Evaluation timeout (expected ${TIMEOUT / 1000}s).`, { ...payload });
    } else {
      const { response } = _.get(result, 'evaluate', {});

      // Bug Fix: Some of the fields reset due to the response from REQUEST_CHART
      // We only care about the Figures and tables here anyway.
      response.data = _.pick(response.data, [
        ...CHART_FIGURE_ID_ARRAY,
        'InputTable',
        'OutputTable1',
        'OutputTable2',
      ]);

      yield put(actions.receiveData(response));
    }
  } catch (err) {
    const statusCode: number = _.get(err, ['response', 'status']);
    if (statusCode === 401) {
      yield put(API.loginRedirect());
    } else {
      yield put(actions.evaluateError({
        field: 'all',
        status: _.get(err, ['response', 'status'], '-1'),
        message: _.get(err, ['message'], 'Unknown error'),
      }));

      // Rollbar.error(`Unexpected Evaluation Error: ${err.message}`, { ...payload });
    }
  }
}

export function* evaluateData(action: any): Generator<*, *, *> {
  // Debounce this action by 500ms
  yield call(delay, EVALUATE_DELAY);

  const payloadData = yield select(getValuesForEval);
  // const step: string = yield select(getCurrentStep);
  const payload = buildPayload(action, payloadData);
  const field: string = _.get(action, ['payload', 'field']);

  try {
    const result = yield race({
      evaluate: call(API.evaluateData, payload),
      timeout: call(delay, TIMEOUT),
    });

    if (_.get(result, 'timeout')) {
      yield put(actions.evaluateError({
        field,
        status: '-1',
        message: `Evaluation took longer than expected (<${TIMEOUT / 1000}s).`,
      }));

      // Rollbar.warning(`Evaluation timeout (expected ${TIMEOUT / 1000}s).`, { ...payload });
    } else {
      const { response } = _.get(result, 'evaluate', {});
      // fix message.type
      const fixedResponse = _.set(response, ['message', 'type'], 'SUCCESS');
      yield put(actions.receiveData(fixedResponse));
    }
  } catch (err) {
    const statusCode: number = _.get(err, ['response', 'status']);
    if (statusCode === 401) {
      yield put(API.loginRedirect());
    } else {
      yield put(actions.evaluateError({
        field,
        status: _.get(err, ['response', 'status'], '-1'),
        message: _.get(err, ['message'], 'Unknown error'),
      }));

      // Rollbar.error(`Unexpected Evaluation Error: ${err.message}`, { ...payload });
    }
  }
}

export function* watchStepProgression(): Generator<*, *, *> {
  while (yield take([constants.JUMP_STEP, constants.STEP_FORWARD, constants.AUTO_COMPLETE])) {
    const currentStep = yield select(getCurrentStep);

    if (currentStep === 'STEP_ANALYSIS') {
      const values = yield select(getFieldValues);

      if (!_.get(values, ['Figure1', 'currentValue'])) {
        yield spawn(asyncCancelOnTake, evaluateReport, [],
          (asyncAction: any): boolean => {
            if ([
              constants.CHANGE_VALUE, constants.JUMP_STEP,
              constants.STEP_FORWARD, constants.STEP_BACKWARD,
              constants.CANCEL_CHART_DATA,
            ].includes(asyncAction.type)) {
              return _.get(asyncAction, ['currentStep'], '') !== 'STEP_ANALYSIS';
            }
            return false;
          },
        );
      }
    }
  }
}

export function* handleValidate(action: AppActions): Generator<*, *, *> {
  const field = _.get(action, ['payload', 'field']);
  const value = _.get(action, ['payload', 'value'], 0);
  const isSelection = _.get(action, ['payload', 'isSelection'], false);
  const revert = _.get(action, ['payload', 'revert'], false);

  try {
    // passthrough for selection dropdowns/ tables
    if (!isSelection) {
      yield call(delay, 500);
      const validator = yield call(createValidator, isNumber);

      let fixedValue = value;
      if (_.isString(value) && value[0] === '.') {
        fixedValue = `0${value}`;
      }

      // TODO: proper validator framekwork
      const decimalCount = countDecimals(fixedValue);
      if (decimalCount > MAX_DECIMAL_PLACES) {
        yield put(actions.validationError({
          field, message: `Must be less than ${MAX_DECIMAL_PLACES + 1} decimal places.`,
        }));
      } else {
        const valid = yield call(validator.test, fixedValue);
        const message = validator.report(value);

        if (valid || revert) {
          // yield put(actions.evaluateData({ field, value: fixedValue }));
          // yield spawn(asyncCancelOnTake, evaluateData, [action], constants.VALIDATION_ERROR);
        } else {
          yield put(actions.validationError({ field, message }));
        }
      }
    }
  } catch (e) {
    // Rollbar.critical('Unexpected Validation Error', { field, value });
    console.log(e); // eslint-disable-line no-console
  }
}

export function* downloadPdf(): Generator<*, *, *> {
  try {
    yield put(actions.requestPDF());
    const values = yield select(getFieldValues);
    const data = buildPayload('', values);

    const designName = yield select(getDesignName);

    // Include a saved design name for the generated PDF
    if (designName !== 'Untitled Design') {
      data.design_name = {
        currentValue: designName,
      };
    }

    const result = yield call(API.downloadReportPDF, { data });
    const fileName = `MotionControl_${_.snakeCase(designName)}.pdf`;
    yield put(actions.receivePDF(result, fileName));
  } catch (err) {
    const statusCode: number = _.get(err, ['response', 'status']);
    if (statusCode === 401) {
      yield put(API.loginRedirect());
    } else {
      yield put(actions.apiError({
        status: '-1',
        message: 'Fetch PDF Error',
        err,
      }));
    }
  }
}

export function* handleSavePdf(action: any): Generator<*, *, *> {
  try {
    // log(action);
    const file = new Blob([action.payload.response], { type: 'application/octet-stream' });
    yield downloadBlob(file, action.fileName);
  } catch (err) {
    // log(err);
  }
}

export function* handleOpenDesign(): Generator<*, *, *> {
  try {
    // yield put(appActions.scrollToTop());
    yield put(actions.receiveDesigns({}));
    yield put(designRoutines.fetchAllRecords());
  } catch (err) {
    yield call(errorHelper, err, 'Fetch All Designs Error');
  }
}

// export function* handleOpenDesign(): Generator<*, *, *> {
//   try {
//     // yield put(actions.scrollTop());
//     // yield put(actions.fetchDesigns());
//     // yield put(designRoutines.fetchAllRecords.trigger());

//     // const values: KeyedObject<FieldDataType> = yield select(getValues);
//     // const data = buildData(values);

//     yield put(designRoutines.fetchAllRecords.request());

//     const result = yield call(API.fetchAllRecords);
//     const data = _.get(result, ['response'], []);
//     // log(data);
//     yield put(designRoutines.fetchAllRecords.success(data));
//     yield put(actions.receiveDesigns(data));
//   } catch (err) {
//     const statusCode: number = _.get(err, ['response', 'status']);
//     if (statusCode === 401) {
//       yield put(API.loginRedirect());
//     } else {
//       yield put(designRoutines.fetchAllRecords.failure(`API Error ${statusCode}`));
//       yield put(actions.apiError({
//         status: '-1',
//         message: 'Fetch All Designs Error',
//       }));
//     }
//   } finally {
//     yield put(designRoutines.fetchAllRecords.fulfill());
//   }
// }

export function* handleFetchDesign(action: any): Generator<*, *, *> {
  try {
    const result = yield call(API.fetchRecord, action.payload);
    const payload = _.get(result, ['response'], {});
    yield put(actions.receiveDesign(payload));
    // yield put(actions.receiveData(payload));
    // yield put(notify.openSuccess());
  } catch (err) {
    const statusCode: number = _.get(err, ['response', 'status']);
    if (statusCode === 401) {
      yield put(API.loginRedirect());
    } else {
      yield put(actions.apiError({
        status: '-1',
        message: 'Fetch All Designs Error',
      }));
    }
  }
}

export function* handleSaveDesign(action: AppActions): Generator<*, *, *> {
  const recordName = _.get(action, 'payload', '');
  const state = yield select(getFieldValues);

  const payload = {
    actionEvent: { recordName },
    data: buildPayload('', state),
  };

  try {
    const result = yield race({
      evaluate: call(API.saveDesignRecord, payload),
      timeout: call(delay, TIMEOUT),
    });

    if (_.get(result, 'timeout')) {
      yield put(actions.apiError({
        status: '-1',
        message: `Save Design took longer than expected (<${TIMEOUT / 1000}s).`,
      }));

      // Rollbar.warning(`Save Design timeout (expected ${TIMEOUT / 1000}s).`, { ...payload });
    // } else {
      // yield put(notify.saveSuccess());
    }
  } catch (err) {
    const statusCode: number = _.get(err, ['response', 'status']);
    if (statusCode === 401) {
      yield put(API.loginRedirect());
    } else {
      yield put(actions.apiError({
        status: '-1',
        message: 'Save Design Error',
      }));

      // Rollbar.error(`Unexpected error saving design: ${err.message}`, { ...payload });
    }
  }
}

export function* watchDesignActions(): Generator<*, *, *> {
  yield all([
    takeLatest(constants.NEW_DESIGN, fetchInitialData),
    takeLatest(constants.CHANGE_VALUE, handleValidate),
    // take([constants.JUMP_STEP, constants.STEP_FORWARD], watchStepProgression),
    // takeLatest([constants.STEP_RESET], evaluateData),
    takeLatest(constants.DOWNLOAD_PDF, downloadPdf),
    takeLatest(constants.RECEIVE_PDF, handleSavePdf),
    // takeLatest(constants.EXPORT_BOM_EXCEL, downloadBOMCVS),
    // takeLatest(constants.DOWNLOAD_SCHEMATIC, downloadSchematic),
    takeLatest(constants.SAVE_DESIGN, handleSaveDesign),
    takeLatest(constants.OPEN_DESIGN, handleOpenDesign),
    // takeLatest(constants.FETCH_DESIGN, handleFetchDesign),
    // takeLatest(constants.REQUEST_USER, fetchUserData),
    // takeLatest(constants.REQUEST_PARTS, fetchPartsData),

    helperAPICallRouter('fetchAllRecords'),
    helperAPICallRouter('fetchRecord'),

    helperAPICallRouter('saveDesignRecord'),
    helperAPICallRouter('updateRecordName'),
    helperAPICallRouter('copyDesign'),
    helperAPICallRouter('deleteDesign'),
    helperAPICallRouter('updateDesign'),

  ]);
}

export default function* initializeDesignActions(): Generator<*, *, *> {
  yield* watchDesignActions();
  yield all([
    put(actions.newDesign(true)), // silent = true so the inital load doesn't hit GA
    watchStepProgression(),
  ]);
}



// WEBPACK FOOTER //
// ./src/sagas/design.js